import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { StatusCode } from "api/protocols";
import { useHistory } from "react-router-dom";
import { useAppDispatch } from "hooks/redux";

import Page from "components/page/Page";
import Card from "components/card/Card";
import Form from "components/form/Form";
import FormLayout from "components/form/FormLayout";
import Input from "components/form/input/Input";
import TimePick from "components/timePick/TimePick";
import { Select } from "components/select/";
import { IModule, ILocation } from "redux/reducers/models/reservations";
import {
  GetReservationLocation,
  GetReservationModuleAll,
  IIntervalSection,
  PostReservationTemplate,
} from "api/rpc/2024-04/facilityAdmin/reservation/reservation";
import { showError } from "redux/actions/ui";
import SelectNew from "components/select/SelectNew";
import Callout from "components/callout/Callout";
import { unitLabel } from "./unitLabel";

interface ILocationInterval {
  location_id: number;
  title: string;
  start_time: string;
  end_time: string;
  interval: number;
}

export interface INewTemplateState {
  name: string;
  modules: Array<IModule>;
  locations: Array<ILocation>;
  selectedModuleId: number;
  selectedLocations: Array<number>;
  web_booking_enabled: boolean;
  credit_card_required: boolean;
  interval_sections: IIntervalSection[];
  locationIntervals: Array<ILocationInterval>;
}

const minimumIntervalLength = "1";
const intervalOptions = [30, 60];

export default function ReservationTemplateNew() {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { Option } = Select;
  const [state, setState] = useState<INewTemplateState>({
    name: "",
    modules: [],
    locations: [],
    selectedModuleId: null,
    selectedLocations: [],
    web_booking_enabled: false,
    credit_card_required: false,
    interval_sections: [
      {
        start_time: "07:00",
        end_time: "19:30",
        interval: 30,
      },
    ],
    locationIntervals: [],
  });

  useEffect(() => {
    void loadModules();
    void loadLocations();
  }, []);

  async function loadModules() {
    const moduleRes = await GetReservationModuleAll({}, true);

    if (moduleRes.status !== StatusCode.OK) {
      return;
    }

    setState(prevState => ({
      ...prevState,
      modules: moduleRes.data,
      selectedModuleId: prevState.selectedModuleId ?? moduleRes.data?.[0]?.id ?? null,
    }));
  }

  async function loadLocations() {
    const locationRes = await GetReservationLocation({}, true);

    if (locationRes.status !== StatusCode.OK) {
      return;
    }

    setState(prevState => ({ ...prevState, locations: locationRes.data }));
  }

  useEffect(() => {
    if (!state.selectedModuleId) {
      return;
    }

    const moduleLocations = state.locations.filter(
      location => location.reservation_module_id === state.selectedModuleId,
    );

    setState(prevState => ({
      ...prevState,
      locationIntervals: moduleLocations.map(location => {
        const existing = prevState.locationIntervals.find(item => item.location_id === location.id);
        return (
          existing ?? {
            location_id: location.id,
            title: location.title,
            start_time: "07:00",
            end_time: "19:30",
            interval: 30,
          }
        );
      }),
    }));
  }, [state.selectedModuleId, state.locations]);

  async function postTemplate() {
    if (!state.selectedModuleId) {
      dispatch(showError(t("Select a module")));
      return;
    }

    if (state.locationIntervals.length === 0) {
      dispatch(showError(t("This module has no locations")));
      return;
    }

    const templateRes = await PostReservationTemplate(
      {
        name: state.name,
        module_id: state.selectedModuleId,
        start_time: "9:00",
        end_time: "8:00",
        web_booking_enabled: state.web_booking_enabled,
        credit_card_required: state.credit_card_required,
        locations: state.locationIntervals.map(location => ({
          location_id: location.location_id,
          interval_sections: [
            {
              start_time: location.start_time,
              end_time: location.end_time,
              interval: location.interval,
            },
          ],
        })),
      },
      true,
    );

    if (templateRes.status !== StatusCode.OK) {
      dispatch(
        showError(
          templateRes.status === StatusCode.BAD_REQUEST
            ? String(templateRes.data)
            : t("Error creating reservation template"),
        ),
      );
      return;
    }

    const createdId = templateRes.data?.id ?? templateRes.data?.data?.id;
    if (!createdId) {
      history.push("/admin/settings/reservations/templates");
      return;
    }

    history.push(`/admin/settings/reservations/templates/${createdId}`);
  }

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const { id } = e.target;
    let value = e.target.value;
    if (id === "interval" && Number(value) < 0) {
      value = minimumIntervalLength;
    }

    setState(prevState => ({ ...prevState, [id]: value }));
  }

  function handleDropDownChange(value: number, property: string) {
    setState(prevState => ({ ...prevState, [property]: value }));
  }

  function handleUpdateLocationInterval(locationId: number, key: string, value: string | number) {
    setState(prevState => ({
      ...prevState,
      locationIntervals: prevState.locationIntervals.map(location =>
        location.location_id === locationId ? { ...location, [key]: value } : location,
      ),
    }));
  }

  const primaryAction = {
    content: t("Create"),
    action: () => postTemplate(),
  };

  return (
    <Page
      narrow
      title={t("New Activity Template")}
      primaryAction={primaryAction}
      breadcrumbs={[
        {
          prefix: true,
          label: t("Back to Templates"),
          url: "/admin/settings/reservations/templates",
        },
      ]}
    >
      <Card>
        <Card.Section>
          <Form>
            <FormLayout>
              <FormLayout.Group>
                <Input value={state.name ?? ""} label={t("Name")} id="name" onChange={handleInputChange} />
                <Select
                  label={t("Activity Type")}
                  onChange={(value: number) => handleDropDownChange(value, "selectedModuleId")}
                  defaultValue={state.modules[0]?.id}
                >
                  {state.modules?.map((module: IModule, index: number) => {
                    return (
                      <Option key={index} value={module.id} name={module.title}>
                        {module.title}
                      </Option>
                    );
                  })}
                </Select>
              </FormLayout.Group>
            </FormLayout>
          </Form>
        </Card.Section>

        <Card.Section title={t("{{name}} Hours", { name: unitLabel(state.modules?.find(module => module.id === state.selectedModuleId)?.title, t("Location")) })}>
          {state.selectedModuleId && state.locationIntervals.length === 0 ? (
            <Callout
              type="info"
              title={t("No {{name}}", { name: unitLabel(state.modules?.find(module => module.id === state.selectedModuleId)?.title, t("Location")) })}
              content={t("Add a {{name}} before creating a template.", {
                name: unitLabel(state.modules?.find(module => module.id === state.selectedModuleId)?.title, t("Location")),
              })}
            />
          ) : (
            state.locationIntervals.map(location => (
              <div key={location.location_id}>
                <div className="flex justify-between">
                  <span className="interval-section-title">{location.title}</span>
                </div>
                <FormLayout.Group>
                  <TimePick
                    value={location.start_time}
                    onChange={time => time && handleUpdateLocationInterval(location.location_id, "start_time", time)}
                    label={t("Start Time")}
                    size="large"
                  />
                  <TimePick
                    value={location.end_time}
                    onChange={time => time && handleUpdateLocationInterval(location.location_id, "end_time", time)}
                    label={t("End Time")}
                    size="large"
                  />
                  <SelectNew
                    id={`interval_${location.location_id}`}
                    label={t("Interval (minutes)")}
                    value={location.interval}
                    onChange={interval => handleUpdateLocationInterval(location.location_id, "interval", interval)}
                    options={intervalOptions.map(interval => ({ id: interval, label: interval }))}
                    placeholder={t("Select Interval")}
                  />
                </FormLayout.Group>
              </div>
            ))
          )}
        </Card.Section>
      </Card>
    </Page>
  );
}
